import { Routes } from '../types/constants';
import { createCheckoutAttemptId } from './checkout-attempt';

export const JOIN_ENTRY_POINTS = [
    'home_hero',
    'nz_combat_sports',
    'pricing',
    'paywall',
    'login',
    'contact',
    'legacy_register',
    'legacy_payment',
    'direct'
] as const;

export type JoinEntryPoint = typeof JOIN_ENTRY_POINTS[number];

const entryPointSet = new Set<string>(JOIN_ENTRY_POINTS);
const checkoutAttemptSessionKey = 'subscription_checkout_attempt';
const allowedReturnPaths = new Set<string>([Routes.WATCH, Routes.VIDEOS]);

interface CheckoutAttemptSession {
    checkoutAttemptId: string;
    offeringId: string;
}

export const normalizeJoinEntryPoint = (value: string | null | undefined): JoinEntryPoint => (
    value && entryPointSet.has(value) ? value as JoinEntryPoint : 'direct'
);

const parseInternalReturnTo = (value: string | null | undefined): URL | null => {
    if (!value) {
        return null;
    }

    try {
        const origin = typeof window === 'undefined' ? 'https://combattv.local' : window.location.origin;
        const isRootRelative = value.startsWith('/') && !value.startsWith('//');
        const isAbsoluteHttpUrl = /^https?:\/\//i.test(value);
        if (!isRootRelative && !isAbsoluteHttpUrl) {
            return null;
        }
        const parsed = new URL(value, origin);
        if (parsed.origin !== origin) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

/** Preserve any same-origin page after a normal sign-in without permitting an open redirect. */
export const getSafeInternalReturnTo = (
    value: string | null | undefined,
    fallback = Routes.VIDEOS
): string => {
    const parsed = parseInternalReturnTo(value);
    return parsed ? `${parsed.pathname}${parsed.search}${parsed.hash}` : fallback;
};

/** Prevent external redirects and checkout loops after a successful payment. */
export const getSafeReturnTo = (
    value: string | null | undefined,
    fallback = Routes.VIDEOS
): string => {
    const parsed = parseInternalReturnTo(value);
    if (!parsed || !allowedReturnPaths.has(parsed.pathname)) {
        return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const buildJoinUrl = ({
    offeringId,
    market,
    source,
    returnTo
}: {
    offeringId?: string | null;
    market?: string | null;
    source?: JoinEntryPoint;
    returnTo?: string | null;
} = {}): string => {
    const params = new URLSearchParams();
    if (offeringId?.trim()) params.set('plan', offeringId.trim());
    if (market?.trim()) params.set('market', market.trim().toLowerCase());
    if (source) params.set('source', source);
    if (returnTo) params.set('returnTo', getSafeReturnTo(returnTo));
    const query = params.toString();
    return `${Routes.JOIN}${query ? `?${query}` : ''}`;
};

const readCheckoutAttemptSession = (): CheckoutAttemptSession | null => {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(checkoutAttemptSessionKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<CheckoutAttemptSession>;
        return parsed.checkoutAttemptId && parsed.offeringId
            ? { checkoutAttemptId: parsed.checkoutAttemptId, offeringId: parsed.offeringId }
            : null;
    } catch {
        return null;
    }
};

const storeCheckoutAttemptSession = (session: CheckoutAttemptSession) => {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(checkoutAttemptSessionKey, JSON.stringify(session));
    } catch {
        // Checkout remains usable when mobile privacy settings disable browser storage.
    }
};

export const storeJoinCheckoutAttempt = (offeringId: string, checkoutAttemptId: string) => {
    storeCheckoutAttemptSession({ offeringId, checkoutAttemptId });
};

export const getOrCreateJoinCheckoutAttempt = (offeringId: string): string => {
    const existing = readCheckoutAttemptSession();
    if (existing) {
        if (existing.offeringId !== offeringId) {
            storeCheckoutAttemptSession({ checkoutAttemptId: existing.checkoutAttemptId, offeringId });
        }
        return existing.checkoutAttemptId;
    }
    const checkoutAttemptId = createCheckoutAttemptId();
    storeCheckoutAttemptSession({ checkoutAttemptId, offeringId });
    return checkoutAttemptId;
};

export const replaceJoinCheckoutAttempt = (offeringId: string): string => {
    const checkoutAttemptId = createCheckoutAttemptId();
    storeCheckoutAttemptSession({ checkoutAttemptId, offeringId });
    return checkoutAttemptId;
};

export const clearJoinCheckoutAttempt = () => {
    if (typeof sessionStorage !== 'undefined') {
        try {
            sessionStorage.removeItem(checkoutAttemptSessionKey);
        } catch {
            // A completed checkout must not be blocked by unavailable browser storage.
        }
    }
};
